import { formatNumber, formatNumberWithCurrency } from '@eika-infrastruktur/format';

import { FormatConfig } from '../types';

export const UNIT_MAP: FormatConfig = {
    year: 'år',
    percent: '%',
    currency: 'kr',
    int: '',
};

export const Utils = {
    parseNumeric: (val: string, allowDecimals: boolean): number => {
        if (allowDecimals) {
            const cleaned = val.replace(/[^\d.,-]/g, '').replace(',', '.');
            const parsed = parseFloat(cleaned);
            return Number.isNaN(parsed) ? 0 : parsed;
        }
        return parseInt(val.replace(/[^0-9]/g, ''), 10) || 0;
    },

    formatCurrency: (val: number): string => {
        return formatNumberWithCurrency(val, UNIT_MAP.currency, 0);
    },

    formatAsYear: (val: number): string => {
        return `${val.toString()} ${UNIT_MAP.year}`;
    },

    formatPercent: (val: number): string => {
        return `${formatNumber(val)} ${UNIT_MAP.percent}`;
    },

    toggleDisplay: (root: HTMLElement, id: string, show: boolean): void => {
        const el = root.querySelector(`#${id}`) as HTMLElement;
        if (el) el.style.display = show ? 'block' : 'none';
    },

    toggleClass: (root: HTMLElement, id: string, className: string, show: boolean): void => {
        const el = root.querySelector(`#${id}`) as HTMLElement;
        if (el) el.classList.toggle(className, show);
    },

    setInnerText: (root: HTMLElement, id: string, text: string): void => {
        const el = root.querySelector(`#${id}`) as HTMLElement;
        if (el) el.innerText = text;
    },
    toggleAriaLive: (root: HTMLElement, turnOff: boolean): void => {
        const statusEl = root.querySelector(`[role="status"]`) as HTMLElement;
        if (!statusEl) return;
        if (turnOff) {
            statusEl.setAttribute('aria-live', 'off');
        } else {
            statusEl.removeAttribute('aria-live');
        }
    },
};

export function calculateEffectiveInterestRate(nominalRate: number): number {
    const r = nominalRate / 1200;
    return 100 * ((1 + r) ** 12 - 1);
}

export function calculateNominalInterestRate(effectiveRate: number): number {
    const eff = effectiveRate / 100;
    const rate = (1 + eff) ** (1 / 12) - 1;
    return 1200 * rate;
}

export function calculateInstallmentAmount(loan: number, rate: number, years: number): number {
    const monthlyRate = rate / 1200;
    const totalMonths = 12 * years;
    return (loan * monthlyRate * (1 + monthlyRate) ** totalMonths) / ((1 + monthlyRate) ** totalMonths - 1);
}

export type ActualNominalRateResult = {
    actualNominalRate: number;
    totalInstallmentCosts: number;
};

type LoanAccumulator = {
    totalInterestAmount: number;
    remainingLoan: number;
    totalRemainingLoan: number;
};

export function calculateActualNominalRate(
    remainingLoan: number,
    installmentAmount: number,
    rate: number,
    installmentFee: number,
    establishmentFee: number,
    years: number,
): ActualNominalRateResult {
    const months: number = 12 * years;

    const calculateLoan = (
        monthsLeft: number,
        acc: LoanAccumulator = {
            totalInterestAmount: 0,
            remainingLoan,
            totalRemainingLoan: remainingLoan,
        },
    ): LoanAccumulator => {
        if (monthsLeft === 0) return acc;

        const interestForPeriod = (acc.remainingLoan * rate) / 1200;
        const newRemainingLoan = acc.remainingLoan - (installmentAmount - interestForPeriod);

        return calculateLoan(monthsLeft - 1, {
            totalInterestAmount: acc.totalInterestAmount + interestForPeriod + installmentFee,
            remainingLoan: newRemainingLoan,
            totalRemainingLoan: acc.totalRemainingLoan + newRemainingLoan,
        });
    };

    const { totalInterestAmount, totalRemainingLoan } = calculateLoan(months);

    const actualNominalRate: number = (1200 * (totalInterestAmount + establishmentFee)) / totalRemainingLoan;

    return {
        actualNominalRate,
        totalInstallmentCosts: totalInterestAmount,
    };
}
